import React, { useEffect, useState } from 'react';
import { inject, observer } from 'mobx-react';

import Page from 'app/components/page/Page';
import { Box, Callout, Card, Grid, Heading, Text } from 'core/components';
import { PLAN_TYPES } from 'app/util/constants';
import { addCommas } from 'app/util/utils';
import AccountTeamCard from 'app/views/licenses/AccountTeamCard';
import PageHeading from 'app/components/page/PageHeading';
import { getEditionText } from 'app/stores/sudo/util';
import NmsDeviceUsageCard from 'app/views/licenses/NmsDeviceUsageCard';
import makeCancelable, { CanceledError } from 'core/util/cancelablePromise';
import LabelValue from '../LabelValue';
import PlanCard from '../PlanCard';
import SyntheticsPlanCard from '../SyntheticsPlanCard';
import MetricsPlanCard from '../MetricsPlanCard';
import TrafficCostsPlanCardV2 from './TrafficCostsPlanCardV2';
import ServiceProviderAnalyticsCardV2 from './ServiceProviderAnalyticsCardV2';
import MkpPlanCardV2 from './MkpPlanCardV2';
import KentikProtectCard from './KentikProtectCard';
import TrafficPakPlanCard from './TrafficPakPlans/TrafficPakPlanCard';

function getCapacityColor(capacity) {
  if (!Number.isFinite(capacity)) {
    return undefined;
  }

  if (capacity >= 0.85) {
    return 'danger';
  }
  if (capacity >= 0.7) {
    return 'warning';
  }
  return undefined;
}

function LicensesV2({ $auth, $companySettings, $cost, $setup, $syn }) {
  const [usedTrafficCostsSnapshots, setUsedTrafficCostsSnapshots] = useState(0);
  const [contactAccountTeamDialogIsOpen, setContactAccountTeamDialogIsOpen] = useState(false);

  useEffect(() => {
    $setup.getPlans();

    // make sure we have up-to-date counts
    $setup.fetchSettings(true);

    $companySettings.loadAssetCounts();
    $syn.plan.loadUsageData({ force: true });

    const snapshotCountRequest = makeCancelable($cost.fetchSnapshotCount());

    snapshotCountRequest.promise
      .then((usedSnapshots) => {
        setUsedTrafficCostsSnapshots(parseInt(usedSnapshots) || 0);
      })
      .catch((error) => {
        if (!(error instanceof CanceledError)) {
          throw error;
        }
      });

    return () => {
      snapshotCountRequest.cancel();
    };
  }, [$cost, $companySettings, $setup, $syn]);

  const openContactAccountTeamDialog = () => {
    setContactAccountTeamDialogIsOpen(true);
  };

  const closeContactAccountTeamDialog = () => {
    setContactAccountTeamDialogIsOpen(false);
  };

  const getPlansByType = (planType) => $setup.plans.filter(({ plan_type, active }) => plan_type === planType && active);

  // Account limits
  const maxDashboards = $auth.getPermission('dashboards.max') || 'Unlimited';
  const maxSubscriptions = $auth.getPermission('reportSubscriptions.max') || 'Unlimited';

  // Plans by type
  const npakPlan = getPlansByType(PLAN_TYPES.nmspak.code);
  const cloudPlans = getPlansByType(PLAN_TYPES.cloud.code);
  const legacyPlans = getPlansByType(PLAN_TYPES.legacy.code);
  const edgePlans = getPlansByType(PLAN_TYPES.edge.code);
  const corePlans = getPlansByType(PLAN_TYPES.core.code);
  const flowPakPlans = getPlansByType(PLAN_TYPES.flowpak.code);
  const syntheticPlans = getPlansByType(PLAN_TYPES.ksynth.code);
  const metricsPlans = getPlansByType(PLAN_TYPES.metrics.code);
  const universalPakPlans = getPlansByType(PLAN_TYPES.universalpak.code);
  const trafficCostsPlans = getPlansByType(PLAN_TYPES.trafficcosts.code);

  // MKP Info
  const hasAdvancedMode = $auth.hasPermission('subtenancy.advancedMode');
  const numTenants = $companySettings.assetCounts?.mkpTenants ?? 0;
  const maxTenants = $auth.getPermission('subtenancy.maxTenants');

  // Service Provider Analytics Info
  const hasOTT = $auth.hasPermission('ott.enabled');
  const hasCDN = $auth.hasPermission('cdn.enabled');
  const hasMarketIntel = $auth.hasPermission('marketIntel.enabled');
  const showServiceProviderAnalyticsCard = hasOTT || hasCDN || hasMarketIntel;

  // Traffic Costs Snapshots usage
  const usedSnapshots = usedTrafficCostsSnapshots;

  return (
    <Page title="Licenses" hideSubnav scrolls>
      <PageHeading title="Licenses" />
      <AccountTeamCard
        isBlock
        isContactDialogOpen={contactAccountTeamDialogIsOpen}
        openContactAccountTeamDialog={openContactAccountTeamDialog}
        closeContactAccountTeamDialog={closeContactAccountTeamDialog}
      />
      <Box>
        {/* Edition Section */}
        <Heading level={4}>Edition</Heading>
        <Card p={2} mb={0} borderRadius="8px" position="relative" zIndex="5">
          <Grid gridTemplateColumns="repeat(3, 1fr)" gridTemplateRows="1fr" mb={2} gridColumnGap="8px">
            <Box>
              {$auth.isActiveTrial && (
                <Box>
                  <Heading level={1} m={0}>
                    Trial
                  </Heading>
                </Box>
              )}

              {!$auth.isActiveTrial && (
                <Box>
                  <Heading level={1} m={0}>
                    Kentik <Text color="warning">{getEditionText($companySettings.edition)}</Text>
                  </Heading>
                </Box>
              )}

              <Text as="div" muted small mb={2}>
                Account #{$auth.getActiveUserProperty('company_id')}
              </Text>
            </Box>
            {$companySettings.edition && (
              <>
                <Box mr={5}>
                  <LabelValue
                    label="Dashboards"
                    value={`${$companySettings.assetCounts?.dashboards ?? 0} of ${maxDashboards ?? 0}`}
                    valueTextProps={{
                      // disabling eslint error and others like it that use getCapacityColor because the util does a check for a finite number
                      // eslint-disable-next-line no-unsafe-optional-chaining
                      color: getCapacityColor($companySettings.assetCounts?.dashboards / maxDashboards)
                    }}
                  />
                  <LabelValue
                    label="Scheduled Reports"
                    value={`${$companySettings.assetCounts?.reportSubscriptions ?? 0} of ${maxSubscriptions ?? 0}`}
                    valueTextProps={{
                      color: getCapacityColor(
                        // eslint-disable-next-line no-unsafe-optional-chaining
                        $companySettings.assetCounts?.reportSubscriptions / maxSubscriptions
                      )
                    }}
                  />
                  <LabelValue
                    label="Users"
                    mb={0}
                    value={`${$companySettings.assetCounts?.users} of ${$auth.getActiveUserProperty('company.company_max_user') || 'Unknown'}`}
                  />
                </Box>
                <Box>
                  <LabelValue
                    label="Custom Dimensions"
                    value={
                      $companySettings.assetCounts
                        ? `${addCommas($companySettings.assetCounts?.custom_dimension_count)} of ${addCommas(
                            $companySettings.assetCounts?.custom_dimension_limit
                          )}`
                        : undefined
                    }
                    valueTextProps={{
                      color: getCapacityColor(
                        // eslint-disable-next-line no-unsafe-optional-chaining
                        $companySettings.assetCounts?.custom_dimension_count /
                          // eslint-disable-next-line no-unsafe-optional-chaining
                          $companySettings.assetCounts?.custom_dimension_limit
                      )
                    }}
                  />
                  <LabelValue
                    label="Custom Populators"
                    value={
                      $companySettings.assetCounts
                        ? `${addCommas($companySettings.assetCounts?.populator_count)} of ${addCommas(
                            $companySettings.assetCounts?.populator_limit
                          )}`
                        : undefined
                    }
                    valueTextProps={{
                      color: getCapacityColor(
                        // eslint-disable-next-line no-unsafe-optional-chaining
                        $companySettings.assetCounts?.populator_count /
                          // eslint-disable-next-line no-unsafe-optional-chaining
                          $companySettings.assetCounts?.populator_limit
                      )
                    }}
                  />
                </Box>
              </>
            )}
          </Grid>

          {$auth.isActiveTrial && (
            <Box maxWidth={350}>
              <Callout intent="primary" p={2}>
                <Text fontWeight="bold">Need higher asset limits or plan capacity?</Text> Contact sales to upgrade your
                company to Kentik Pro/Premier.
              </Callout>
            </Box>
          )}
        </Card>

        {/* EXTENSIONS & ADDONS */}
        {/* MKP */}
        <MkpPlanCardV2 hasAdvancedMode={hasAdvancedMode} numTenants={numTenants} maxTenants={maxTenants} zIndex="4" />

        {/* Traffic Costs Snapshots */}
        {trafficCostsPlans.map((plan) => (
          <TrafficCostsPlanCardV2 key={plan.id} plan={plan} usedTrafficCostsSnapshots={usedSnapshots} zIndex="3" />
        ))}

        {/* Service Provider Analytics */}
        {showServiceProviderAnalyticsCard && (
          <ServiceProviderAnalyticsCardV2 hasOTT={hasOTT} hasCDN={hasCDN} hasMarketIntel={hasMarketIntel} zIndex="2" />
        )}

        {/* Kentik Protect */}
        <KentikProtectCard kentikEdition={$companySettings.edition} zIndex="1" />

        {/* Plans Section */}
        <Heading mt={3} level={4}>
          Plans
        </Heading>

        {cloudPlans.map((plan) => (
          <PlanCard
            key={plan.id}
            {...plan}
            mb={2}
            trialDaysRemaining={$auth.trialDaysRemaining}
            openContactAccountTeamDialog={openContactAccountTeamDialog}
          />
        ))}

        {edgePlans.map((plan) => (
          <TrafficPakPlanCard
            key={plan.id}
            {...plan}
            mb={2}
            trialDaysRemaining={$auth.trialDaysRemaining}
            openContactAccountTeamDialog={openContactAccountTeamDialog}
          />
        ))}

        {flowPakPlans.map((plan) => (
          <TrafficPakPlanCard
            key={plan.id}
            {...plan}
            mb={2}
            trialDaysRemaining={$auth.trialDaysRemaining}
            openContactAccountTeamDialog={openContactAccountTeamDialog}
          />
        ))}

        {universalPakPlans.map((plan) => (
          <PlanCard
            key={plan.id}
            {...plan}
            mb={2}
            trialDaysRemaining={$auth.trialDaysRemaining}
            openContactAccountTeamDialog={openContactAccountTeamDialog}
          />
        ))}

        {metricsPlans.map((plan) => (
          <MetricsPlanCard key={plan.id} {...plan} mb={2} trialDaysRemaining={$auth.trialDaysRemaining} />
        ))}

        {npakPlan.map((plan) => (
          <NmsDeviceUsageCard
            key={plan.id}
            {...plan}
            mb={2}
            trialDaysRemaining={$auth.trialDaysRemaining}
            openContactAccountTeamDialog={openContactAccountTeamDialog}
          />
        ))}

        {syntheticPlans.map((plan) => (
          <SyntheticsPlanCard
            key={plan.id}
            {...plan}
            mb={2}
            trialDaysRemaining={$auth.trialDaysRemaining}
            openContactAccountTeamDialog={openContactAccountTeamDialog}
          />
        ))}

        {corePlans.map((plan) => (
          <PlanCard key={plan.id} {...plan} mb={2} trialDaysRemaining={$auth.trialDaysRemaining} />
        ))}

        {legacyPlans.map((plan) => (
          <PlanCard key={plan.id} {...plan} mb={2} trialDaysRemaining={$auth.trialDaysRemaining} />
        ))}
      </Box>
    </Page>
  );
}

export default inject('$auth', '$cost', '$setup', '$syn', '$companySettings')(observer(LicensesV2));
